<script setup lang="ts">
import { computed } from "vue";
import { assertNever } from "../lib/assertNever";
import { useExportToast } from "../composables/useExportToast";
import {
  exportToastButtons,
  exportToastMessage,
  type ExportToastButton,
} from "../composables/useExportToast.update";

const { notice, dismiss, showInFolder, openFile } = useExportToast();

const visible = computed(() => notice.value.kind !== "hidden");
const isError = computed(() => notice.value.kind === "error");
const message = computed(() => exportToastMessage(notice.value));
const buttons = computed(() => exportToastButtons(notice.value));

function runAction(id: ExportToastButton["id"]): void {
  switch (id) {
    case "showInFolder":
      showInFolder();
      return;
    case "open":
      openFile();
      return;
    default:
      assertNever(id);
  }
}
</script>

<template>
  <Teleport to="body">
    <p
      v-if="visible"
      class="export-toast"
      :class="{ 'export-toast--error': isError }"
      :role="isError ? 'alert' : 'status'"
      aria-live="polite"
    >
      <span class="export-toast-message">{{ message }}</span>
      <span class="export-toast-actions">
        <button
          v-for="action in buttons"
          :key="action.id"
          type="button"
          class="export-toast-action"
          :class="{ 'export-toast-action--primary': action.primary }"
          @click="runAction(action.id)"
        >
          {{ action.label }}
        </button>
        <button type="button" class="export-toast-dismiss" @click="dismiss">
          Dismiss
        </button>
      </span>
    </p>
  </Teleport>
</template>

<style scoped>
.export-toast {
  position: fixed;
  z-index: 200;
  top: max(0.75rem, env(safe-area-inset-top, 0px));
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  max-width: min(42rem, calc(100% - 1.5rem));
  margin: 0;
  padding: 0.45rem 0.7rem;
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  background: var(--bg-raised);
  font-family: var(--font-ui);
  font-size: 0.82rem;
  color: var(--text);
}

.export-toast--error {
  border-color: var(--danger);
}

.export-toast-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.export-toast-action,
.export-toast-dismiss {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  font: inherit;
  color: var(--text-muted);
  cursor: pointer;
}

.export-toast-action--primary {
  color: var(--text-strong);
  font-weight: 600;
}

.export-toast-action:hover,
.export-toast-dismiss:hover {
  color: var(--text-strong);
}
</style>
